import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ExternalLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input
} from '@chain-zen/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import { ENABLE_ELD88_APP } from '../../../data/mutations/apps'
import { GET_APPS } from '../../../data/queries/apps'
import { useAuth } from '../../../hooks/use-auth'
import type { AppTypeEnum } from '../../../types/workspace'
import type { AppModalProps } from '../apps-data'

type EnableELD88AppInput = {
  apiKey: string
  appType: AppTypeEnum
}

const formSchema = z.object({
  apiKey: z.string().min(1, 'API Key is required')
})

/**
 * This is the modal for the ELD88 app.
 */
export function ELD88Modal({ isOpen, onClose, appType, app }: AppModalProps) {
  const { selectedWorkspace } = useAuth()
  const workspaceID = selectedWorkspace?.workspace.id || ''
  const [formError, setFormError] = useState('')

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      apiKey: ''
    },
    resolver: zodResolver(formSchema)
  })

  const [enableELD88App, { loading }] = useMutation<{ enableELD88App: { apiKey: string } }, EnableELD88AppInput>(
    ENABLE_ELD88_APP,
    {
      context: {
        headers: {
          'ws-id': workspaceID
        }
      },
      onCompleted: () => {
        toast.success('ELD88 app enabled successfully!')
        handleClose()
      },
      onError: (err) => {
        const errorMessage =
          err.message === 'Invalid API Key'
            ? 'Invalid API key. Please try again.'
            : `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`

        setFormError(errorMessage)
        toast.error(errorMessage)
      },
      refetchQueries: [GET_APPS]
    }
  )

  const handleClose = () => {
    setFormError('')
    form.reset()
    onClose()
  }

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    enableELD88App({
      variables: {
        apiKey: values.apiKey,
        appType: appType as AppTypeEnum
      }
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg w-full max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enable ELD88 for Chain</DialogTitle>
          <DialogDescription>
            Enabling this integration requires an ELD88 account. Enter your ELD88 API key.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="apiKey"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ELD88 API Key</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your ELD88 API Key" {...field} disabled={loading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!!formError && <Alert variant="error" description={formError} />}

            <DialogFooter className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:gap-0">
              <a
                href={app.docs}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
              >
                See detailed steps here
                <ExternalLink className="h-4 w-4" />
              </a>

              <div className="space-x-2">
                <Button variant="outline" onClick={handleClose} type="button">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} onClick={form.handleSubmit(onSubmit)}>
                  {loading ? 'Enabling...' : 'Enable'}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
